import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";

export default function Account() {
  const { user, ready, refreshMe } = useAuth();
  const [orders, setOrders] = useState([]);
  const [fivemId, setFivemId] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (user) {
      setFivemId(user.fivem_id || "");
      api.myOrders().then(setOrders).catch(() => {});
    }
  }, [user]);

  if (ready && !user) return <Navigate to="/" replace />;

  const saveFivem = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      await api.linkFivem(fivemId.trim());
      await refreshMe();
      setMessage("FiveM ID linked.");
    } catch (err) {
      setMessage(err?.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="account-page" className="pt-24 min-h-screen max-w-[1440px] mx-auto px-6 md:px-10 py-16">
      <div className="eyebrow mb-4">/ ACCOUNT · CLEARANCE {user?.is_admin ? "ADMIN" : "CIVILIAN"}</div>
      <h1 className="font-display text-white text-6xl md:text-8xl tracking-tighter leading-[0.9] mb-14">
        {user?.discord_username || "Operator"}<span className="text-[color:var(--accent-red)]">.</span>
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <section className="lg:col-span-5 border border-white/10 bg-[color:var(--bg-surface)] p-6">
          <div className="eyebrow mb-3">/ FIVEM LINK</div>
          <h2 className="font-display text-2xl text-white uppercase mb-4">In-game identity</h2>
          <form onSubmit={saveFivem}>
            <label className="eyebrow block mb-2">FIVEM ID</label>
            <input
              data-testid="account-fivem-input"
              value={fivemId}
              onChange={(e) => setFivemId(e.target.value)}
              placeholder="steam:110000... or in-game name"
              className="terminal-input"
            />
            <button
              type="submit"
              disabled={saving || !fivemId.trim()}
              data-testid="account-save-fivem"
              className="btn-brutal mt-4 h-11 px-6 border border-white/15 text-white font-mono text-[11px] tracking-[0.24em] uppercase disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save FiveM ID"}
            </button>
            {message && <div className="mt-3 font-mono text-[11px] text-zinc-300">{message}</div>}
          </form>
        </section>

        <section className="lg:col-span-7">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="eyebrow mb-2">/ RECEIPTS</div>
              <h2 className="font-display text-2xl text-white uppercase">Order history</h2>
            </div>
            <div className="font-mono text-[10px] text-zinc-500">{orders.length} TOTAL</div>
          </div>
          {orders.length === 0 ? (
            <div className="font-mono text-zinc-500 text-sm border border-dashed border-white/10 py-16 text-center">
              // no orders yet.
            </div>
          ) : (
            <div className="border border-white/10">
              {orders.map((o) => (
                <div key={o.id} data-testid={`order-row-${o.id}`} className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-white/5 last:border-0 font-mono text-[12px]">
                  <div className="col-span-2 text-zinc-500">#{o.id.slice(0, 8).toUpperCase()}</div>
                  <div className="col-span-5 text-white truncate">{o.product_name}</div>
                  <div className="col-span-2 text-zinc-400">£{Number(o.amount).toFixed(2)}</div>
                  <div className="col-span-2 text-zinc-400">{o.payment_method.toUpperCase()}</div>
                  <div className={`col-span-1 text-right ${o.status === "paid" || o.status === "delivered" ? "text-emerald-400" : "text-amber-400"}`}>
                    {o.status.toUpperCase()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
